type SaveFilePicker = (options: {
  suggestedName: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<{
  createWritable(): Promise<{ write(data: Blob): Promise<void>; close(): Promise<void> }>;
}>;

export type FileKind = { extension: string; mime: string; description: string };

export const PDF_FILE: FileKind = {
  extension: '.pdf',
  mime: 'application/pdf',
  description: 'PDF document',
};
export const ZIP_FILE: FileKind = {
  extension: '.zip',
  mime: 'application/zip',
  description: 'Zip archive',
};
export const WORD_FILE: FileKind = {
  extension: '.docx',
  mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  description: 'Word document',
};

/**
 * Save a file the page already holds. Where the browser offers a save dialog (Chrome, Edge),
 * the file is written directly: download managers such as IDM intercept ordinary downloads,
 * cannot read the in-memory copy, and produce an empty file. Elsewhere it falls back to a
 * normal download.
 */
export async function saveFile(data: Blob, title: string, kind: FileKind): Promise<void> {
  const suggestedName = `${title.replace(/[\\/:*?"<>|]+/g, '-').trim()}${kind.extension}`;
  const picker = (window as Window & { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  if (picker) {
    let handle: Awaited<ReturnType<SaveFilePicker>>;
    try {
      handle = await picker({
        suggestedName,
        types: [{ description: kind.description, accept: { [kind.mime]: [kind.extension] } }],
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      throw error;
    }
    const writable = await handle.createWritable();
    await writable.write(data);
    await writable.close();
    return;
  }
  const url = URL.createObjectURL(data);
  const link = Object.assign(document.createElement('a'), { href: url, download: suggestedName });
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
